import type { LucideIcon } from 'lucide-react'
import {
  Bell,
  Cable,
  CalendarCheck,
  CalendarClock,
  ChartNoAxesColumn,
  CodeXml,
  Database,
  DatabaseZap,
  FileText,
  FileSignature,
  Files,
  FlaskConical,
  Gauge,
  HardHat,
  HeartPulse,
  LayoutGrid,
  MessageSquareText,
  Network,
  NotebookPen,
  PhoneOutgoing,
  ReceiptText,
  Server,
  Settings as SettingsIcon,
  ShieldCheck,
  Stethoscope,
  TestTubeDiagonal,
  UserCheck,
  Wrench,
} from 'lucide-react'

import type { FeatureFlags } from '@/types/configuration'

export type DestinationSection =
  | 'Data'
  | 'Actions'
  | 'Settings'
  | 'Knowledge'
  | 'Admin'

/**
 * Gates that are not capabilities or feature flags. Each maps to the hook the
 * matching route guard reads, resolved once in `useNavigationDestinations`.
 */
export type DestinationRole =
  | 'globalAdmin'
  | 'careforceOrgAdmin'
  | 'permissionsAdmin'

export interface Destination {
  id: string
  /** What the user reads, and the first thing the search matches on. */
  label: string
  route: string
  icon: LucideIcon
  section: DestinationSection
  /**
   * Extra search terms. Load-bearing wherever the label and the route disagree
   * (`/data/patients` is labelled Clients) or the app's word for something is
   * not the word in the user's head (Sessions are workflow runs).
   */
  keywords?: string[]
  capability?: string
  featureFlag?: keyof FeatureFlags
  role?: DestinationRole
}

/**
 * Every static destination the command palette can reach, with the gates that
 * decide whether this user may see it.
 *
 * The gates are not cosmetic. Routes like `/duties/metrics` and `/permissions`
 * sit behind guards that bounce a user who reaches them without the right
 * grant, so an entry here has to reproduce its route guard's predicate — see
 * `src/config/mainRoutes.tsx` for the authoritative pairing. Offering a page
 * that ejects you is worse than not offering it.
 *
 * Duplicated from `AppSidebar.tsx` on purpose, for now: the sidebar is the
 * app's primary navigation and this list has yet to prove itself. Once it has,
 * the sidebar should render from this array and the duplication disappears.
 */
export const NAVIGATION_DESTINATIONS: Destination[] = [
  // ---------------------------------------------------------------- Data
  {
    id: 'clients',
    label: 'Clients',
    route: '/data/patients',
    icon: UserCheck,
    section: 'Data',
    capability: 'data.clients.view',
    // The route says patients; the UI says Clients. Both have to find it.
    keywords: ['patients', 'people', 'members'],
  },
  {
    id: 'visits',
    label: 'Visits',
    route: '/data/visits',
    icon: Network,
    section: 'Data',
    capability: 'data.visits.view',
    keywords: ['encounters'],
  },
  {
    id: 'appointments',
    label: 'Appointments',
    route: '/data/appointments',
    icon: CalendarCheck,
    section: 'Data',
    capability: 'data.appointments.view',
    keywords: ['schedule', 'calendar'],
  },
  {
    id: 'claims',
    label: 'Claims',
    route: '/data/claims',
    icon: ReceiptText,
    section: 'Data',
    capability: 'data.claims.view',
    keywords: ['billing', 'claim lines'],
  },
  {
    id: 'providers',
    label: 'Providers',
    route: '/data/providers',
    icon: Stethoscope,
    section: 'Data',
    capability: 'data.providers.view',
    keywords: ['doctors', 'clinicians'],
  },
  {
    id: 'lab-results',
    label: 'Lab Results',
    route: '/data/lab-results',
    icon: TestTubeDiagonal,
    section: 'Data',
    capability: 'data.labResults.view',
    keywords: ['labs', 'tests', 'clinical'],
  },
  {
    id: 'chronic-conditions',
    label: 'Chronic Conditions',
    route: '/data/chronic-conditions',
    icon: HeartPulse,
    section: 'Data',
    capability: 'data.chronicConditions.view',
    keywords: ['diagnoses', 'conditions', 'clinical'],
  },
  {
    id: 'files',
    label: 'Files',
    route: '/data/files',
    icon: Files,
    section: 'Data',
    capability: 'data.files.view',
    keywords: ['uploads', 'attachments'],
  },
  {
    id: 'data-sources',
    label: 'Imports',
    route: '/data/ingestion',
    icon: DatabaseZap,
    section: 'Data',
    capability: 'data.ingestion.view',
    keywords: [
      'ingestion',
      'imports',
      'pipelines',
      'runs',
      'data sources',
      'cobalt',
      'sync',
    ],
  },
  {
    id: 'facts',
    label: 'Facts Catalogue',
    route: '/data/facts',
    icon: Database,
    section: 'Data',
    capability: 'data.facts.view',
    keywords: ['schema', 'fields', 'catalog'],
  },
  {
    id: 'snippets',
    label: 'Snippets',
    route: '/snippets',
    icon: CodeXml,
    section: 'Data',
    // Both boundaries, matching `useSnippetsEnabled`: the org's rollout, then
    // who inside it may use them.
    capability: 'snippets.view',
    featureFlag: 'snippets',
    keywords: ['prompt fragments'],
  },

  // ------------------------------------------------------------- Actions
  {
    id: 'duties',
    label: 'Duties',
    route: '/duties',
    icon: HardHat,
    section: 'Actions',
    capability: 'duties.view',
    keywords: ['campaigns', 'jobs'],
  },
  {
    id: 'duties-metrics',
    label: 'Metrics',
    route: '/duties/metrics',
    icon: ChartNoAxesColumn,
    section: 'Actions',
    // Both gates, deliberately — the same pair the route guard applies. The
    // org's rollout flag is not enough on its own; the matrix row has to be
    // granted too.
    capability: 'duties.metrics.view',
    featureFlag: 'duty_metrics',
    keywords: ['analytics', 'duty metrics', 'stats', 'dashboard'],
  },
  {
    id: 'calls',
    label: 'Calls',
    route: '/calls',
    icon: PhoneOutgoing,
    section: 'Actions',
    capability: 'calls.list',
    keywords: ['call logs', 'phone'],
  },
  {
    id: 'messages',
    label: 'Messages',
    route: '/messages',
    icon: MessageSquareText,
    section: 'Actions',
    capability: 'messages.list',
    keywords: ['sms', 'texts'],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    route: '/notifications',
    icon: Bell,
    section: 'Actions',
    // No gate, matching the route in `mainRoutes.tsx`: the relay addresses
    // every row to the signed-in user, so the page can only ever show
    // notifications that are already theirs.
    keywords: ['inbox', 'alerts', 'unread', 'bell'],
  },
  {
    id: 'signing-documents',
    label: 'Documents',
    route: '/documents',
    icon: FileSignature,
    section: 'Actions',
    role: 'globalAdmin',
    keywords: ['signed', 'collected', 'forms', 'signatures'],
  },
  {
    id: 'sessions',
    label: 'Sessions',
    route: '/sessions',
    icon: CalendarClock,
    section: 'Actions',
    capability: 'sessions.list',
    keywords: ['workflow runs', 'history'],
  },
  {
    id: 'phone-lines',
    label: 'Active Lines',
    route: '/phone-lines',
    icon: LayoutGrid,
    section: 'Actions',
    // Both, and they are not redundant. The capability is what the route
    // guard actually reads, so without it a Careforce admin whose org has not
    // granted `phoneLines.board` would be offered the page and then bounced.
    // The role is what the sidebar reads, and it carries a separate intent:
    // this is a Careforce-wide board, so it stays hidden outside that org even
    // for someone holding the grant. Neither predicate implies the other —
    // `useCapability` has no global-admin bypass.
    capability: 'phoneLines.board',
    role: 'careforceOrgAdmin',
    keywords: ['phone lines', 'numbers', 'board'],
  },

  // ------------------------------------------------------------ Settings
  {
    id: 'connections',
    label: 'Connections',
    route: '/connections',
    icon: Cable,
    section: 'Settings',
    capability: 'connections.status',
    // Workflows are a section of this page rather than a destination of their
    // own, so the words people search for them by have to land here.
    keywords: ['integrations', 'workflows', 'automations'],
  },
  {
    id: 'machines',
    label: 'Machines',
    route: '/machines',
    icon: Server,
    section: 'Settings',
    capability: 'machines.grid',
    keywords: ['workers', 'compute', 'grid'],
  },
  {
    id: 'evaluation-rubrics',
    label: 'Evaluation Rubrics',
    route: '/evaluation-rubrics',
    icon: TestTubeDiagonal,
    section: 'Settings',
    capability: 'config.evaluationRubrics.view',
    featureFlag: 'evaluation_rubrics',
    keywords: ['rubrics', 'llm judge', 'call analysis', 'evals'],
  },
  {
    id: 'tools',
    label: 'Tools',
    route: '/tools',
    icon: Wrench,
    section: 'Settings',
    role: 'globalAdmin',
    keywords: ['tool admin'],
  },
  {
    id: 'evals',
    label: 'Evals',
    // The tab route, not `/evaluations`: the palette matches open tabs by
    // route, and `/evaluations` opens under this one.
    route: '/evaluations/memory',
    icon: FlaskConical,
    section: 'Settings',
    // `ProtectedCareforceOrgRoute` — the active org has to be Careforce, since
    // the evals are product-wide rather than this org's.
    role: 'careforceOrgAdmin',
    keywords: ['evaluations', 'memory evals', 'test cases'],
  },

  // ----------------------------------------------------------- Knowledge
  {
    id: 'notes',
    label: 'Notes',
    route: '/notes',
    icon: NotebookPen,
    section: 'Knowledge',
    // Without both of these the sidebar points Notes at the legacy app. An
    // external URL is not a tab, so the palette simply drops the entry.
    capability: 'notes.list',
    featureFlag: 'meeting_notes_table',
    keywords: ['meeting notes', 'meetings'],
  },
  {
    id: 'documents',
    label: 'Documents',
    route: '/docs',
    icon: FileText,
    section: 'Knowledge',
    keywords: ['docs', 'writing'],
  },

  // --------------------------------------------------------------- Admin
  {
    id: 'billing',
    label: 'Usage',
    route: '/billing',
    icon: Gauge,
    section: 'Admin',
    capability: 'billing.view',
    keywords: ['billing', 'invoice', 'cost', 'spend'],
  },
  {
    id: 'config',
    label: 'Config',
    route: '/config',
    icon: SettingsIcon,
    section: 'Admin',
    capability: 'config.orgSelect',
    keywords: ['configuration', 'settings'],
  },
  {
    id: 'permissions',
    label: 'Permissions',
    route: '/permissions',
    icon: ShieldCheck,
    section: 'Admin',
    role: 'permissionsAdmin',
    keywords: ['capabilities', 'roles', 'access', 'matrix'],
  },
]

/** Render order for the palette's groups. */
export const DESTINATION_SECTIONS: DestinationSection[] = [
  'Actions',
  'Data',
  'Knowledge',
  'Settings',
  'Admin',
]
